import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import axios from "api/axios";
import PageShell, { SectionCard } from "components/patch/PageShell";
import SimpleTable from "components/patch/SimpleTable";

const money = (value) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(Number(value || 0));

const getRows = (payload) => {
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

export default function PayrollDisbursement() {
  const [runs, setRuns] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedRun, setSelectedRun] = useState("");
  const [format, setFormat] = useState("bank_csv");
  const [preview, setPreview] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const selectedRunData = useMemo(
    () => runs.find((run) => String(run.id) === String(selectedRun)),
    [runs, selectedRun]
  );

  const loadRuns = async () => {
    const res = await axios.get("/payroll/disbursements/payroll-runs");
    setRuns(getRows(res.data));
  };

  const loadBatches = async () => {
    const res = await axios.get("/payroll/disbursements");
    const data = res.data?.data?.data || res.data?.data || [];
    setBatches(Array.isArray(data) ? data : []);
  };

  const loadAll = async () => {
    setLoading(true);
    setError("");
    try {
      await Promise.all([loadRuns(), loadBatches()]);
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to load disbursement workspace.");
    } finally {
      setLoading(false);
    }
  };

  const previewRun = async () => {
    if (!selectedRun) return;
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const res = await axios.get(`/payroll/disbursements/payroll-runs/${selectedRun}/preview`);
      setPreview(res.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to preview payment schedule.");
    } finally {
      setLoading(false);
    }
  };

  const createBatch = async (allowWarnings = false) => {
    if (!selectedRun) return;
    setLoading(true);
    setError("");
    setMessage("");
    try {
      await axios.post("/payroll/disbursements", {
        payroll_run_id: selectedRun,
        format,
        allow_warnings: allowWarnings,
      });
      setMessage("Payment batch created successfully.");
      await loadBatches();
    } catch (err) {
      const payload = err?.response?.data;
      setError(payload?.message || "Unable to create payment batch.");
      if (payload?.summary || payload?.items) {
        setPreview({
          payroll_run: selectedRunData,
          summary: payload.summary,
          items: payload.items || [],
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const openBatch = async (batch) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`/payroll/disbursements/${batch.id}`);
      setSelectedBatch(res.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to open payment batch.");
    } finally {
      setLoading(false);
    }
  };

  const approveBatch = async () => {
    if (!selectedBatch?.batch?.id) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`/payroll/disbursements/${selectedBatch.batch.id}/approve`);
      setMessage("Batch approved.");
      await openBatch(selectedBatch.batch);
      await loadBatches();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to approve batch.");
    } finally {
      setLoading(false);
    }
  };

  const submitBatch = async () => {
    if (!selectedBatch?.batch?.id) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`/payroll/disbursements/${selectedBatch.batch.id}/submit`);
      setMessage("Batch marked as submitted.");
      await openBatch(selectedBatch.batch);
      await loadBatches();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to submit batch.");
    } finally {
      setLoading(false);
    }
  };

  const bulkMarkPaid = async () => {
    if (!selectedBatch?.batch?.id) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`/payroll/disbursements/${selectedBatch.batch.id}/items/bulk-status`, {
        status: "paid",
      });
      setMessage("All eligible payment rows marked paid.");
      await openBatch(selectedBatch.batch);
      await loadBatches();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to update payment rows.");
    } finally {
      setLoading(false);
    }
  };

  const exportBatch = () => {
    if (!selectedBatch?.batch?.id) return;
    window.open(
      `${axios.defaults.baseURL}/payroll/disbursements/${selectedBatch.batch.id}/export`,
      "_blank"
    );
  };

  useEffect(() => {
    loadAll();
  }, []);

  const previewColumns = [
    { key: "pf_no", label: "PF No" },
    { key: "staff_name", label: "Staff" },
    { key: "bank_name", label: "Bank" },
    { key: "account_number", label: "Account" },
    { key: "amount", label: "Net Pay", render: (row) => money(row.amount) },
    {
      key: "issues",
      label: "Validation",
      render: (row) =>
        row.issues?.length ? (
          <Chip color="error" size="small" label={row.issues.join("; ")} />
        ) : (
          <Chip color="success" size="small" label="Payable" />
        ),
    },
  ];

  const batchColumns = [
    { key: "batch_no", label: "Batch No" },
    { key: "month", label: "Month" },
    { key: "format", label: "Format" },
    { key: "total_staff", label: "Staff" },
    { key: "total_amount", label: "Amount", render: (row) => money(row.total_amount) },
    { key: "status", label: "Status", render: (row) => <Chip size="small" label={row.status} /> },
    {
      key: "actions",
      label: "Action",
      render: (row) => (
        <Button size="small" onClick={() => openBatch(row)}>
          Open
        </Button>
      ),
    },
  ];

  const itemColumns = [
    { key: "pf_no", label: "PF No" },
    { key: "staff_name", label: "Staff" },
    { key: "bank_name", label: "Bank" },
    { key: "account_number", label: "Account" },
    { key: "amount", label: "Amount", render: (row) => money(row.amount) },
    { key: "status", label: "Status", render: (row) => <Chip size="small" label={row.status} /> },
    { key: "failure_reason", label: "Issue/Failure" },
  ];

  return (
    <PageShell
      title="Payroll Disbursement"
      subtitle="Generate bank/TSA/GIFMIS payment schedules, approve batches, export payment files, and track paid/failed payroll disbursements."
    >
      <Stack spacing={2.5}>
        {message ? (
          <Alert severity="success" onClose={() => setMessage("")}>
            {message}
          </Alert>
        ) : null}
        {error ? (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        <SectionCard
          title="Create Payment Batch"
          subtitle="Use only approved/finalized payroll runs for disbursement."
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <FormControl fullWidth size="small">
                <InputLabel>Payroll Run</InputLabel>
                <Select
                  label="Payroll Run"
                  value={selectedRun}
                  onChange={(e) => setSelectedRun(e.target.value)}
                >
                  {runs.map((run) => (
                    <MenuItem key={run.id} value={run.id}>
                      #{run.id} — {run.month} — {run.status || "ready"} — {run.items_count} staff
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Export Format</InputLabel>
                <Select
                  label="Export Format"
                  value={format}
                  onChange={(e) => setFormat(e.target.value)}
                >
                  <MenuItem value="bank_csv">Bank CSV</MenuItem>
                  <MenuItem value="gifmis_csv">GIFMIS CSV</MenuItem>
                  <MenuItem value="tsa_csv">TSA CSV</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Button variant="outlined" disabled={!selectedRun || loading} onClick={previewRun}>
                  Preview
                </Button>
                <Button
                  variant="contained"
                  disabled={!selectedRun || loading}
                  onClick={() => createBatch(false)}
                >
                  Create Batch
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {preview?.summary ? (
            <Box mt={2}>
              <Divider sx={{ mb: 2 }} />
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" mb={2}>
                <Chip label={`Total: ${preview.summary.total_count || 0}`} />
                <Chip color="success" label={`Payable: ${preview.summary.payable_count || 0}`} />
                <Chip
                  color={(preview.summary.critical_count || 0) > 0 ? "error" : "default"}
                  label={`Critical: ${preview.summary.critical_count || 0}`}
                />
                <Chip label={`Amount: ${money(preview.summary.payable_amount)}`} />
              </Stack>
              <SimpleTable columns={previewColumns} rows={preview.items || []} />
              {(preview.summary.critical_count || 0) > 0 ? (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Critical issues should be fixed before payment. Only use forced creation when
                  management approves exceptions.
                  <Button sx={{ ml: 2 }} size="small" onClick={() => createBatch(true)}>
                    Create With Warnings
                  </Button>
                </Alert>
              ) : null}
            </Box>
          ) : null}
        </SectionCard>

        <SectionCard
          title="Payment Batches"
          subtitle="Open a batch to approve, export, submit, or update payment statuses."
        >
          <SimpleTable columns={batchColumns} rows={batches} />
        </SectionCard>

        {selectedBatch?.batch ? (
          <SectionCard
            title={`Batch ${selectedBatch.batch.batch_no}`}
            subtitle={`Status: ${selectedBatch.batch.status} | Staff: ${
              selectedBatch.summary?.total_count || 0
            } | Paid: ${selectedBatch.summary?.paid_count || 0}`}
          >
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" mb={2}>
              <Button variant="outlined" onClick={approveBatch} disabled={loading}>
                Approve
              </Button>
              <Button variant="outlined" onClick={exportBatch}>
                Export CSV
              </Button>
              <Button variant="outlined" onClick={submitBatch} disabled={loading}>
                Mark Submitted
              </Button>
              <Button variant="contained" onClick={bulkMarkPaid} disabled={loading}>
                Mark All Paid
              </Button>
            </Stack>
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} md={3}>
                <Typography variant="button">
                  Total: {money(selectedBatch.summary?.total_amount)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="button">
                  Paid: {money(selectedBatch.summary?.paid_amount)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="button">
                  Failed: {money(selectedBatch.summary?.failed_amount)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search in browser"
                  placeholder="Use Ctrl+F for now"
                  disabled
                />
              </Grid>
            </Grid>
            <SimpleTable columns={itemColumns} rows={selectedBatch.batch.items || []} />
          </SectionCard>
        ) : null}
      </Stack>
    </PageShell>
  );
}
